import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  MARKIT_HOTLINE_NUMBER,
  mostRecentSubscription,
} from "@markit/common.utils";
import { MixpanelContext } from "../../context/AnalyticsService";
import { API } from "../../API";
import { formatPhoneNumber } from "../../utils/FormatPhoneNumber";
import AlertContainer from "../Containers/AlertContainer";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { testStripe } from "../../utils/stripeAccountUtils";

type PaymentFailedButtonModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  location: string;
  button?: React.ReactNode;
  noClose?: boolean;
  addOnClose?: () => void;
};

const PaymentFailedButtonModal = (props: PaymentFailedButtonModalProps) => {
  const { isVisible, setIsVisible, location, button, noClose, addOnClose } =
    props;
  const { accountData } = useSelector(getAccountState).account;
  const mixpanel = useContext(MixpanelContext);
  const [invoiceUrl, setInvoiceUrl] = useState("");

  const [alertText, setAlertText] = useState({
    heading: "",
    subHeading: "",
  });

  const styles = {
    descriptionText: {
      fontSize: 14,
      fontWeight: "500",
      color: Colors.BLACK,
    },
  };

  const subscription = useMemo(
    () => mostRecentSubscription(accountData),
    [accountData]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (isVisible && subscription) {
      mixpanel.track("Show Update Payment Info Prompt", {
        location: location,
      });
      // fetch the invoice link for the user
      const { invoiceUrl } = await (testStripe(accountData.uid)
        ? API.user.sendTestPaymentFailedInvoice
        : API.user.sendPaymentFailedInvoice)({
        uid: accountData.uid,
        subscriptionId: subscription.id,
      });
      setInvoiceUrl(invoiceUrl);
    }
  }, [location, mixpanel, isVisible, accountData.uid, subscription]);

  const toggleModal = useCallback(() => {
    setIsVisible(!isVisible);
    if (addOnClose) {
      addOnClose();
    }
  }, [addOnClose, isVisible, setIsVisible]);

  const sendInvoiceOnPress = useCallback(async () => {
    if (invoiceUrl !== "") {
      window.open(invoiceUrl);
      setIsVisible(false);
    } else {
      setAlertText({
        heading: "Cannot view invoice at this time.",
        subHeading: `Please text our hotline (${formatPhoneNumber(
          MARKIT_HOTLINE_NUMBER
        )}) with the following message: \nUpdate Billing Info ${
          accountData.username
        }`,
      });
    }
  }, [accountData.username, invoiceUrl, setIsVisible]);

  return (
    <>
      {button}
      {isVisible ? (
        <PopupModalContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <Icon
                icon="ion:card"
                height={35}
                style={{ color: Colors.RED3 }}
              />
              <span style={{ fontSize: 16, fontWeight: "500" }}>
                Update Billing Info
              </span>
            </div>
          }
          subHeaderComp={
            <div className="ColumnNormal" style={{ gap: 10 }}>
              <span style={{ fontSize: 14 }}>
                Your payment method on file for your Markit+ subscription
                failed. You will not be able to access Markit+ features until
                you pay your outstanding balance.
              </span>
              <span style={styles.descriptionText}>
                View your invoice below or visit the billing section to cancel
                your subscription.
              </span>
            </div>
          }
          valueComp={
            <div className="ColumnCentering" style={{ gap: 14 }}>
              <RectangleButton
                buttonLabel={<span>Pay Failed Invoice</span>}
                onPress={sendInvoiceOnPress}
                altTextColor={Colors.WHITE}
                altColor={Colors.BLACK}
                altPaddingVert={14}
              />
              <AlertContainer
                headerComp={alertText.heading}
                subHeaderComp={
                  alertText.subHeading !== "" ? alertText.subHeading : undefined
                }
                closeModal={() => setAlertText({ heading: "", subHeading: "" })}
                hideModal={
                  alertText.heading === "" && alertText.subHeading === ""
                }
              />
            </div>
          }
          noExit
          closeModal={noClose ? () => {} : toggleModal}
          closeOnOutsidePress
          modalContentWidth={PopupModalContainerWidth.SMALL}
        />
      ) : null}
    </>
  );
};

export default PaymentFailedButtonModal;
